import { type FormEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Smokebreak } from '@wk/components-react16';
import { IconLoader2, IconPlayerPlay } from '@tabler/icons-react';

import {
  useSubmitSiteComplianceEvaluationMutation,
  type SubmitEvaluationResponse,
} from '@/domains/siteCompliance';
import useOnGoingEvaluationsStore from '@/features/ongoingEvaluations/stores/ongoingEvaluationsStore';
import { reactPlugin } from '@/config/telemetry';
import type { SiteComplianceApiError } from '@/lib/siteComplianceApi';
import useToast from '@/shared/stores/toastStore';

interface RunEvaluationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (response: SubmitEvaluationResponse) => void;
  siteId: string | null;
  siteName?: string | null;
}

const getErrorMessage = (error: SiteComplianceApiError) => {
  const status = error.response?.status ?? 0;
  const responseData = error.response?.data;

  if (status === 403) {
    return 'You do not have permission to start evaluations.';
  }

  if (status >= 400 && status < 500) {
    return (
      responseData?.title ??
      responseData?.detail ??
      (typeof responseData?.error === 'string' ? responseData.error : null) ??
      'Unable to start evaluation.'
    );
  }

  return 'Unable to start evaluation. Please try again later.';
};

const RunEvaluationDialog = ({
  isOpen,
  onClose,
  onSubmitted,
  siteId,
  siteName,
}: RunEvaluationDialogProps) => {
  const { showToast } = useToast(
    useShallow((state) => ({
      showToast: state.showToast,
    })),
  );
  const { addOngoingEvaluation, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        addOngoingEvaluation: state.addOngoingEvaluation,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );
  const mutation = useSubmitSiteComplianceEvaluationMutation({
    onError: (error) => {
      const message = getErrorMessage(error);

      console.error('Site Compliance evaluation submit failed', {
        error,
        jobId: null,
        evaluationId: null,
        siteId,
      });

      showToast('error', {
        title: 'Unable to start evaluation',
        message,
      });
    },
    onSuccess: (response) => {
      addOngoingEvaluation({
        applicationName: siteName ?? siteId ?? 'Site Compliance site',
        args: [],
        bitId: response.siteId,
        environmentName: 'site',
        evaluationId: response.evaluationId,
        force: true,
        jobId: response.jobId,
        message: response.message ?? undefined,
        output: response.output
          ? {
              bitId: response.siteId,
              duration: response.output.duration ?? '',
              message: response.output.message ?? '',
              recordId:
                response.evaluationId ??
                response.output.recordId ??
                response.output.evaluationId ??
                '',
              runId: response.output.runId ?? response.jobId,
              status: response.output.status ?? response.status,
            }
          : null,
        siteId: response.siteId,
        source: 'site-compliance',
        startedAt: new Date().toISOString(),
        status: response.status,
        statusUrl: response.statusUrl,
      });
      setIsOngoingEvaluationsOpen(true);

      reactPlugin.trackEvent(
        { name: 'SiteComplianceJobSubmitted' },
        {
          force: true,
          jobId: response.jobId,
          siteId: response.siteId,
        },
      );

      showToast('success', {
        title: 'Evaluation job accepted',
        message: `Job ${response.jobId} was accepted for ${response.siteId}.`,
      });

      onSubmitted?.(response);
      onClose();
    },
  });

  const submitEvaluation = () => {
    if (!siteId || mutation.isPending) {
      return;
    }

    mutation.mutate({ siteId });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitEvaluation();
  };

  const handleClose = () => {
    if (mutation.isPending) {
      return;
    }

    onClose();
  };

  return (
    <Smokebreak isOpen={isOpen} position="right" onUserRequest={handleClose}>
      <h2
        slot="smokebreakHeader"
        className="truncate p-3 pr-12 text-lg font-semibold"
        title={siteId ?? undefined}
      >
        Run evaluation for {siteId ?? 'site'}
      </h2>
      <form
        slot="smokebreakBody"
        className="flex h-full w-full flex-col gap-4 p-3"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-3 text-sm text-gray-700">
          <p>
            This starts a Site Compliance evaluation against the backend for{' '}
            <span className="font-semibold text-gray-900">{siteId}</span>.
          </p>
        </div>

        <div className="mt-auto grid w-full grid-cols-2 gap-2">
          <button
            type="button"
            className="flex min-h-9 items-center justify-center rounded border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={mutation.isPending}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex min-h-9 items-center justify-center gap-2 rounded border border-[#005b92] bg-[#005b92] px-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            disabled={!siteId || mutation.isPending}
            onClick={submitEvaluation}
          >
            {mutation.isPending ? (
              <IconLoader2 className="animate-spin" size={16} />
            ) : (
              <IconPlayerPlay size={16} />
            )}
            Run evaluation
          </button>
        </div>
      </form>
    </Smokebreak>
  );
};

export default RunEvaluationDialog;
